/**
 * Account settings section — info strip, change email, change password,
 * and 2FA. All operations go through the BetterAuth client.
 *
 * Account deletion lives in <DangerZoneSection /> on the same page, gated
 * by its own modal — single source of truth for that destructive flow,
 * separate from the day-to-day account-edit forms here.
 */
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Shield, Calendar, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, FormTextField, Form, Checkbox } from '@/interfaces/learner/ui';
import { useAuth } from '@/shared/auth/AuthProvider';
import { authClient } from '@/shared/auth/authClient';
import { PasswordStrengthMeter } from '@/shared/auth/PasswordStrengthMeter';
import { Setup2FAModal } from '@/shared/auth/Setup2FAModal';
import { SettingsSection, CollapsibleRow } from './SettingsSection';

// ─── Schemas ────────────────────────────────────────────────────────────────

const EmailChangeSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
});
type EmailChangeFormData = z.infer<typeof EmailChangeSchema>;

// Note: revokeOtherSessions is `z.boolean()` (not `.default(true)`) because
// the zodResolver for react-hook-form mishandles the input/output type
// asymmetry that `.default()` introduces. The default is provided via
// `defaultValues` on useForm instead.
const PasswordChangeFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password must be at most 128 characters'),
    confirmPassword: z.string(),
    revokeOtherSessions: z.boolean(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type PasswordChangeFormData = z.infer<typeof PasswordChangeFormSchema>;

// ─── Component ──────────────────────────────────────────────────────────────

export default function AccountSection() {
  const { user, role, twoFactorEnabled } = useAuth();

  // Collapsible state
  const [emailOpen, setEmailOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);

  // Email form
  const [emailLoading, setEmailLoading] = useState(false);
  const {
    control: emailControl,
    handleSubmit: handleEmailSubmit,
    reset: resetEmailForm,
  } = useForm<EmailChangeFormData>({
    resolver: zodResolver(EmailChangeSchema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  // Password form
  const [passwordLoading, setPasswordLoading] = useState(false);
  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    watch: watchPassword,
  } = useForm<PasswordChangeFormData>({
    resolver: zodResolver(PasswordChangeFormSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      revokeOtherSessions: true,
    },
    mode: 'onBlur',
  });
  const newPasswordValue = watchPassword('newPassword');

  const createdAtRaw = user?.createdAt ?? null;
  const createdAt = createdAtRaw
    ? new Date(createdAtRaw).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Unknown';

  async function handleEmailChange(data: EmailChangeFormData) {
    setEmailLoading(true);
    // callbackURL must be absolute — BetterAuth's verify-email endpoint
    // lives on the API origin, so a relative '/' would resolve to the
    // API host and dump the user on a 404. Send them to /email-verified,
    // which generically handles the post-verify success state and bounces
    // to the dashboard.
    const { error } = await authClient.changeEmail({
      newEmail: data.email,
      callbackURL: `${window.location.origin}/email-verified`,
    });
    if (error) {
      toast.error(error.message ?? 'Failed to update email');
    } else {
      toast.success('Confirmation sent to your current email');
      resetEmailForm();
      setEmailOpen(false);
    }
    setEmailLoading(false);
  }

  async function handlePasswordChange(data: PasswordChangeFormData) {
    setPasswordLoading(true);
    const { error } = await authClient.changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      revokeOtherSessions: data.revokeOtherSessions,
    });
    if (error) {
      toast.error(error.message ?? 'Failed to change password');
    } else {
      toast.success('Password changed successfully');
      resetPasswordForm();
      setPasswordOpen(false);
    }
    setPasswordLoading(false);
  }

  const infoItems = [
    { icon: Mail, label: 'Email', value: user?.email ?? '—' },
    { icon: Shield, label: 'Role', value: role?.slug?.replace('_', ' ') ?? 'Learner' },
    { icon: Calendar, label: 'Member since', value: createdAt },
  ];

  return (
    <SettingsSection title="Account" delay={0.2} cardClassName="space-y-6">
      {/* Info strip */}
      <div className="flex flex-wrap gap-x-8 gap-y-4">
        {infoItems.map(({ icon: Icon, label, value }) => (
          <div
            key={label}
            className="flex min-w-[200px] items-center gap-3 rounded-2xl border px-3.5 py-3"
            style={{
              background: 'var(--color-surface-muted)',
              borderColor: 'var(--color-surface-border)',
            }}
          >
            <div
              className="flex items-center justify-center h-8 w-8 rounded-lg"
              style={{ background: 'var(--color-surface-solid)' }}
            >
              <Icon size={15} strokeWidth={1.75} style={{ color: 'var(--color-text-muted)' }} />
            </div>
            <div>
              <p className="text-meta-muted">{label}</p>
              <p className="text-list-title capitalize">{value}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={{ height: 1, background: 'var(--color-border)' }} />

      {/* Change email */}
      <CollapsibleRow
        label="Change email"
        open={emailOpen}
        onToggle={() => {
          setEmailOpen(!emailOpen);
          if (emailOpen) resetEmailForm();
        }}
      >
        <Form onSubmit={handleEmailSubmit(handleEmailChange)} className="pt-4 space-y-3">
          <div className="max-w-sm">
            <FormTextField
              control={emailControl}
              name="email"
              label="New email address"
              placeholder="Enter your new email address"
              type="email"
            />
          </div>
          <p className="text-meta-muted">
            A confirmation will be sent to your current email — click the link there to apply the
            change.
          </p>
          <div className="flex gap-3">
            <Button type="submit" isDisabled={emailLoading} variant="accent">
              {emailLoading ? 'Sending...' : 'Update email'}
            </Button>
            <Button
              onPress={() => {
                resetEmailForm();
                setEmailOpen(false);
              }}
              isDisabled={emailLoading}
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
        </Form>
      </CollapsibleRow>

      <div style={{ height: 1, background: 'var(--color-border)' }} />

      {/* Change password */}
      <CollapsibleRow
        label="Change password"
        open={passwordOpen}
        onToggle={() => {
          setPasswordOpen(!passwordOpen);
          if (passwordOpen) resetPasswordForm();
        }}
      >
        <Form onSubmit={handlePasswordSubmit(handlePasswordChange)} className="pt-4 space-y-3">
          <div className="max-w-sm space-y-3">
            <FormTextField
              control={passwordControl}
              name="currentPassword"
              label="Current password"
              type="password"
              placeholder="Your current password"
            />
            <FormTextField
              control={passwordControl}
              name="newPassword"
              label="New password"
              type="password"
              placeholder="At least 8 characters"
            />
            <PasswordStrengthMeter password={newPasswordValue ?? ''} />
            <FormTextField
              control={passwordControl}
              name="confirmPassword"
              label="Confirm new password"
              type="password"
              placeholder="Re-enter password"
            />
            <Controller
              control={passwordControl}
              name="revokeOtherSessions"
              render={({ field }) => (
                <Checkbox isSelected={field.value} onChange={field.onChange}>
                  Sign out of all other sessions
                </Checkbox>
              )}
            />
          </div>
          <div className="flex gap-3">
            <Button type="submit" isDisabled={passwordLoading} variant="accent">
              {passwordLoading ? 'Updating...' : 'Update password'}
            </Button>
            <Button
              onPress={() => {
                resetPasswordForm();
                setPasswordOpen(false);
              }}
              isDisabled={passwordLoading}
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
        </Form>
      </CollapsibleRow>

      <div style={{ height: 1, background: 'var(--color-border)' }} />

      {/* Two-factor authentication */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center h-8 w-8 rounded-lg"
            style={{ background: 'var(--color-surface-solid)' }}
          >
            <ShieldCheck
              size={15}
              strokeWidth={1.75}
              style={{
                color: twoFactorEnabled ? 'var(--color-success-text)' : 'var(--color-text-muted)',
              }}
            />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-list-title">Two-factor authentication</p>
              {twoFactorEnabled && <Badge variant="success">Enabled</Badge>}
            </div>
            <p className="text-meta-muted">
              {twoFactorEnabled
                ? 'A 6-digit code is required when you sign in.'
                : 'Adds a 6-digit code from your authenticator app.'}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onPress={() => setTwoFactorModalOpen(true)}>
          {twoFactorEnabled ? 'Manage' : 'Set up 2FA'}
        </Button>
      </div>
      <Setup2FAModal
        isOpen={twoFactorModalOpen}
        onClose={() => setTwoFactorModalOpen(false)}
        isEnabled={twoFactorEnabled}
        onStateChange={() => {
          // Force the session atom to refetch so user.twoFactorEnabled
          // flips on the next render — without this, the badge + row copy
          // wait until the next refetchOnWindowFocus event.
          void authClient.getSession();
        }}
      />

      {/* Delete account lives in the dedicated <DangerZoneSection /> on the
          settings page — single source of truth, single confirmation modal,
          no duplicate UI. */}
    </SettingsSection>
  );
}
